import React, { useState, useEffect } from "react";
import { FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";

// Dynamically import all images
const importAll = (r) =>
  r.keys().map((key) => {
    const module = r(key);
    const src = module.default || module;
    return src;
  });

// Import images from the Gallery folder
const images = importAll(
  require.context("../assets/Gallery", false, /\.(png|jpe?g|webp)$/)
);

const Gallery = () => {
  const [selectedImg, setSelectedImg] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImg) return;

      if (e.key === "Escape") {
        closeModal();
      } else if (e.key === "ArrowLeft") {
        navigate(-1);
      } else if (e.key === "ArrowRight") {
        navigate(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImg, currentIndex]);

  const openModal = (img, index) => {
    setSelectedImg(img);
    setCurrentIndex(index);
  };

  const closeModal = () => {
    setSelectedImg(null);
    setCurrentIndex(0);
  };

  const navigate = (direction) => {
    let newIndex = currentIndex + direction;
    
    // Loop around
    if (newIndex < 0) {
      newIndex = images.length - 1;
    } else if (newIndex >= images.length) {
      newIndex = 0;
    }
    
    setCurrentIndex(newIndex);
    setSelectedImg(images[newIndex]);
  };

  return (
    <div className="p-4">
     {/* Hero Header */}
      <section className="relative bg-primary rounded-xl overflow-hidden mb-16">
        {/* Background Pattern Overlay */}
        <div className="absolute inset-0 bg-bg-pattern bg-cover bg-center opacity-30"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white">
            Gallery
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
            Where clinical science meets emotional healing
          </p>
        </div>
      </section>

      {/* Gallery Grid - More Compact */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2">
        {images.map((src, index) => (
          <div
            key={index}
            className="overflow-hidden rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer bg-gray-100"
            onClick={() => openModal(src, index)}
          >
            <img
              src={src}
              alt={`Gallery-${index + 1}`}
              className="w-full h-32 object-cover transform transition duration-200 hover:scale-110"
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Modal / Lightbox */}
      {selectedImg && (
        <div className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50">
          {/* Close Button */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          >
            <FaTimes size={28} />
          </button>

          {/* Navigation Buttons */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(-1);
            }}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-3"
          >
            <FaChevronLeft size={24} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(1);
            }}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-3"
          >
            <FaChevronRight size={24} />
          </button>

          {/* Image Counter */}
          <div className="absolute top-4 left-4 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded-full z-10">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Main Image */}
          <div className="relative max-w-4xl max-h-full mx-4">
            <img
              src={selectedImg}
              alt={`Gallery-${currentIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>

          {/* Thumbnail Strip */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 max-w-full overflow-x-auto py-2 px-4 bg-black bg-opacity-50 rounded-lg">
            {images.map((img, index) => (
              <img
                key={index}
                src={img}
                alt={`Thumb-${index + 1}`}
                className={`w-12 h-12 object-cover rounded cursor-pointer border-2 transition-all ${
                  index === currentIndex
                    ? "border-white scale-110"
                    : "border-transparent opacity-60 hover:opacity-100"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  openModal(img, index);
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;